import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, MinusCircle } from 'lucide-react';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

export function DeliveryLogTab({ rules }) {
  const { get } = useApi();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [statusFilter, setStatusFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        if (statusFilter) params.set('status', statusFilter);
        if (templateFilter) params.set('template_key', templateFilter);
        const res = await get(`/notification-rules/log?${params.toString()}`);
        if (!cancelled) setData(res || { items: [], total: 0 });
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load log');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, templateFilter]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div className="space-y-3" data-testid="notif-center-log">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground">Status:</span>
          {STATUS_FILTERS.map(sf => (
            <button
              key={sf.value || 'all'}
              onClick={() => { setStatusFilter(sf.value); setPage(1); }}
              data-testid={`notif-center-log-status-${sf.value || 'all'}`}
              className={`px-2.5 py-1 rounded-md border text-xs ${
                statusFilter === sf.value
                  ? 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)]'
                  : 'hover:border-foreground/20 text-muted-foreground'
              }`}
            >{sf.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs ml-auto">
          <span className="text-muted-foreground">Alert:</span>
          <select
            className="border rounded-md text-xs px-2 py-1 bg-background"
            value={templateFilter}
            onChange={e => { setTemplateFilter(e.target.value); setPage(1); }}
            data-testid="notif-center-log-filter"
          >
            <option value="">All</option>
            {rules.map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
            <option value="digest_summary">Digest summary</option>
          </select>
        </div>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Sent</th>
              <th className="text-left px-3 py-2 font-medium">Alert</th>
              <th className="text-left px-3 py-2 font-medium">Recipient</th>
              <th className="text-left px-3 py-2 font-medium">Subject</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : (data.items || []).length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No deliveries match these filters yet.</td></tr>
            ) : data.items.map(row => (
              <tr key={row.log_id} className="border-t" data-testid={`notif-center-log-row-${row.log_id}`}>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{formatTime(row.sent_at)}</td>
                <td className="px-3 py-2 text-xs">{row.template_name}</td>
                <td className="px-3 py-2 text-xs font-mono">{row.to_email}</td>
                <td className="px-3 py-2 text-xs max-w-[300px] truncate" title={row.subject}>{row.subject}</td>
                <td className="px-3 py-2 text-xs"><StatusPill status={row.status} error={row.error} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{data.total || 0} total</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="notif-center-log-prev">Prev</Button>
          <span>Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="notif-center-log-next">Next</Button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, error }) {
  const map = {
    sent:    { Icon: CheckCircle2, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    failed:  { Icon: XCircle,      cls: 'text-red-600     bg-red-50     border-red-200' },
    skipped: { Icon: MinusCircle,  cls: 'text-amber-600   bg-amber-50   border-amber-200' },
  };
  const m = map[status] || map.skipped;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] ${m.cls}`} title={error || ''}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
